import { Component, Host, h, Element, Prop, State, Listen, Watch } from '@stencil/core';
import { mixColorForTheme } from '@aha-app/aha-shared';
import { Shape, Color } from './types';

interface ThemeColor {
  light: string;
  dark: string;
}

// Named colors are colors that design has specified that they will _always_ use in their designs.
const NAMED_COLORS: Record<string, ThemeColor> = {
  peridot: { light: 'var(--aha-peridot-800)', dark: 'var(--aha-peridot-200)' },
  jade: { light: 'var(--aha-jade-700)', dark: 'var(--aha-jade-300)' },
  emerald: { light: 'var(--aha-emerald-600)', dark: 'var(--aha-emerald-400)' },
  topaz: { light: 'var(--aha-topaz-600)', dark: 'var(--aha-topaz-400)' },
  blue: { light: 'var(--aha-blue-500)', dark: 'var(--aha-blue-400)' },
  lapis: { light: 'var(--aha-lapis-500)', dark: 'var(--aha-lapis-400)' },
  amethyst: { light: 'var(--aha-amethyst-500)', dark: 'var(--aha-amethyst-200)' },
  tourmaline: { light: 'var(--aha-tourmaline-500)', dark: 'var(--aha-tourmaline-400)' },
  rhodonite: { light: 'var(--aha-rhodonite-500)', dark: 'var(--aha-rhodonite-300)' },
  garnet: { light: 'var(--aha-garnet-700)', dark: 'var(--aha-garnet-400)' },
  gold: { light: 'var(--aha-gold-600)', dark: 'var(--aha-gold-300)' },
  citrine: { light: 'var(--aha-citrine-800)', dark: 'var(--aha-citrine-500)' },
  gray: { light: 'var(--aha-gray-800)', dark: 'var(--aha-gray-200)' },
};

const paletteColorRegex = new RegExp(/--aha-([a-z]+)-([0-9]+)/);

export interface AhaGraphicIcon extends HTMLAhaGraphicIconElement {}

@Component({
  tag: 'aha-graphic-icon',
  styleUrl: 'aha-graphic-icon.css',
  shadow: false,
})
export class AhaGraphicIcon {
  @Element() host!: HTMLAhaGraphicIconElement;

  private boxElement?: HTMLElement;

  /**
   * Icon to display in the fa-icon component.
   */
  @Prop({ reflect: true }) icon?: string;

  /**
   * Size of the icon's background. The size of the icon itself is a percentage of this size.
   */
  @Prop({ reflect: true }) size?: string;

  /**
   * Size of the icon's background. The size of the icon itself is a percentage of this size.
   */
  @Prop({ reflect: true }) shape: Shape = 'square';

  /**
   * Color of the icon. This can be a color name, a hex value, or a CSS variable.
   */
  @Prop({ reflect: true }) color?: Color | string;

  @Watch('color')
  colorChanged() {
    this.calculateBackgroundColor();
  }

  @State() iconColor?: string;
  @State() backgroundColor?: string;
  @State() filter?: string;

  componentWillLoad() {
    if (this.host.children) {
      // Because this component does not use shadow DOM (because we want to be able to include FA icons), we need to
      // clear out any children that are added to the host element. This is to work around in issue when sharing a web
      // page that uses this component, as the shared web page is captured as fully rendered HTML. When the page is
      // loaded, the saved HTML is rendered and the component is rehydrated. If the saved component has children, the
      // children are rendered in the shared web page alongside the newly mounted children, which is not the desired behavior.
      this.host.innerHTML = '';
    }
  }

  componentDidLoad() {
    this.calculateBackgroundColor();
  }

  @Listen('theme:changed', { target: 'window' })
  handleThemeChanged() {
    this.calculateBackgroundColor();
  }

  setPaletteBackgroundColor(color: string) {
    if (this.useDarkMode) {
      // Ideally we would use the `950` color value, but if it's not available we'll use the `900` or `800` value instead.
      return `var(--aha-${color}-950, var(--aha-${color}-900, var(--aha-${color}-800)))`;
    } else {
      // Ideally we would use the `10` color value, but if it's not available we'll use the `50`, `100`, or `200` value instead.
      return `var(--aha-${color}-10, var(--aha-${color}-50, var(--aha-${color}-100, var(--aha-${color}-200))))`;
    }
  }

  // Set the icon and background colors when a user specified color is one of the NAMED_COLORS and so should always have very specific values.
  setNamedColors(color: string) {
    this.iconColor = this.useDarkMode ? NAMED_COLORS[color].dark : NAMED_COLORS[color].light;
    this.backgroundColor = this.setPaletteBackgroundColor(color);
  }

  // Set the icon and background colors when a user specified color is an aha palette color (but not a NAMED_COLOR).
  setPaletteColors(originalColor: string, colorRoot: string) {
    // If the colorRoot is a NAMED_COLOR, then set the icon color to the correct NAMED_COLOR value.
    // Otherwise set the icon color to the originally specified color.
    this.iconColor = NAMED_COLORS[colorRoot]
      ? this.useDarkMode
        ? NAMED_COLORS[colorRoot].dark
        : NAMED_COLORS[colorRoot].light
      : originalColor;

    this.backgroundColor = this.setPaletteBackgroundColor(colorRoot);
  }

  // Handle all other colors that are not a NAMED_COLOR and are also not an aha palette color.
  setOtherColors(color?: string) {
    // If the color is not a NAMED_COLOR and is also not a palette color, then we do our best to calculate a nice looking value that is in keeping with the saturation and brightness applied for NAMED and palette colors.
    const boxElem = this.boxElement;
    this.iconColor = color;

    // Need to wait a tick for the `this.iconColor` value to flush to the shadowDOM's inner box element's `style` attribute.
    requestAnimationFrame(() => {
      // Wait a tick to get the computed color of the box element. This is necessary because the color of the box element
      // may be a CSS variable, and we need to wait for the rendered value to be available.
      requestAnimationFrame(() => {
        if (!boxElem) return;
        const renderedColor = getComputedStyle(boxElem).getPropertyValue('color');
        this.backgroundColor = mixColorForTheme(renderedColor, 80, this.useDarkMode);

        // When just setting the background color via mixColorForTheme, the background color ends up being a bit washed out
        // compared to similar colors that are in the aha color palette. To remedy this, we want to make the background a
        // little brighter and more saturated. It's still not an exact match, but it feels a bit warmer and more vibrant,
        // which is what we're going for with the new jewel colors.
        this.filter = 'saturate(1.3) brightness(1.04)';
      });
    });
  }

  get useDarkMode() {
    return (
      document.body.classList.contains('dark-mode-allowed') &&
      document.body.dataset.theme === 'dark'
    );
  }

  get isNamedColor() {
    return this.color && NAMED_COLORS[this.color];
  }

  calculateBackgroundColor() {
    // Reset the filter value.
    this.filter = '';

    if (this.color && this.isNamedColor) {
      this.setNamedColors(this.color);
    } else {
      // If the color is from our aha color palette, but is NOT a NAMED_COLOR, then we want to use a specific background color for the icon.
      const paletteColor = this.color && this.color.match(paletteColorRegex);

      if (this.color && paletteColor) {
        // If the color is a CSS variable from our aha color palette, we want to use a specific value for the background color.
        const [_, colorRoot] = paletteColor;
        this.setPaletteColors(this.color, colorRoot);
      } else {
        // If the color is neither a NAMED_COLOR nor an aha palette color, then generate a nice custom background color.
        this.setOtherColors(this.color);
      }
    }
  }

  calculateSize() {
    switch (this.size) {
      case 'small':
        return '20px';
      case 'medium':
        return '30px';
      case 'large':
        return '40px';
      case this.size?.match(/^\d+$/)?.input:
        return `${this.size}px`;
      default:
        return this.size;
    }
  }

  render() {
    return (
      <Host>
        <div
          ref={el => (this.boxElement = el)}
          class="aha-graphic-icon__box"
          style={{
            'color': this.iconColor,
            '--aha-graphic-icon--background-color': this.backgroundColor,
            '--aha-graphic-icon--filter': this.filter,
            '--aha-graphic-icon--size': this.calculateSize(),
          }}
        >
          <i class={['aha-graphic-icon__icon', this.icon].join(' ')}></i>
        </div>
      </Host>
    );
  }
}
